import type { App } from 'vue'

interface LogRecord {
	elapsedMs: number
	stacktrace?: string[]
}

class Logger {
	#logs = [] as LogRecord[]
	#maxSize = 0
	#enabled = false
	#startTimestampMs?: number
	#endTimestampMs?: number
	#tags: string[] = []
	#tagOnce = true

	constructor(maxSize = 1000, enabled = false) {
		this.#maxSize = maxSize
		if (enabled) {
			this.enable()
		}
	}

	enable () {
		this.#startTimestampMs = Date.now()
		this.#endTimestampMs = undefined
		this.#enabled = true
	}

	disable () {
		this.#endTimestampMs = Date.now()
		this.#enabled = false
	}

	tag (values: string | string[], once: boolean | null = null) {
		if (values) {
			if (Array.isArray(values)) {
				this.#tags = this.#tags.concat(values)
			} else {
				this.#tags.push(values)
			}
		}
		if (once !== null) {
			this.#tagOnce = once
		}
	}

	untag (tags: string | string[] | null = null) {
		if (tags === null) {
			this.#tags = []
		} else if (Array.isArray(tags)) {
			this.#tags = this.#tags.filter(t => tags.includes(t))
		} else {
			this.#tags = this.#tags.filter(t => t !== tags)
		}
		if (!this.#tags.length) {
			this.#tagOnce = true
		}
	}

	log (category: string, data = {}) {
		const tag = this.#tags.join(',')
		if (!this.#enabled) {
			return
		}
		if (this.#tagOnce) {
			this.#tags = []
		}
		const elapsedMs = Date.now() - this.#startTimestampMs!!
		const stacktrace = new Error().stack?.split('\n')?.slice(2)
		const record: LogRecord = {
			elapsedMs,
			[ category ]: {
				[ tag ]: data
			},
			stacktrace
		}
		this.#logs.push(record)
		if (this.#logs.length > this.#maxSize) {
			this.#logs.shift()
		}
	}

	dump (withStackTraces = false) {
		const logs = JSON.parse(JSON.stringify(this.#logs)) as LogRecord[]
		if (!withStackTraces) {
			logs.forEach((log) => delete log['stacktrace'])
		}
		console.log({
			startTimestampMs: this.#startTimestampMs,
			endTimestampMs: this.#endTimestampMs,
			logs
		})
		this.#logs = []
		this.#endTimestampMs = undefined
		this.#startTimestampMs = this.#enabled ? Date.now() : undefined;
	}
}

export const logger = new Logger(1000, process.env.NODE_ENV !== 'production')

declare global {
	interface Window {
		__LOGGER__?: typeof logger
	}
}

export default {
	install (app: App, options = {}) {
		app.config.globalProperties.$logger = logger

		app.provide('logger', logger)

		if (process.env.NODE_ENV !== 'production') {
			window.__LOGGER__ = logger
		}
	}
}
